import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { supabase } from '../../lib/supabase';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import SignUpLayout from './SignUpLayout';
import './SignUp.css';

const UserInfo = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { updateProfile } = useProfile();
  const navigate = useNavigate();

  // Check if user has a team
  const checkUserTeam = async (userId: string): Promise<boolean> => {
    try {
      // Check if user is in the users array of any team in the Teams table
      const { data: teamsData, error: teamsError } = await supabase
        .from('Teams')
        .select('id, users')
        .contains('users', [userId]);
      
      if (!teamsError && teamsData && teamsData.length > 0) {
        return true;
      }
      
      // Try alternative approach - get all teams and check manually
      const { data: allTeams, error: allTeamsError } = await supabase
        .from('Teams')
        .select('id, users');
        
      if (!allTeamsError && allTeams && allTeams.length > 0) {
        // Check each team's users array for the userId
        const userTeams = allTeams.filter(team => 
          team.users && Array.isArray(team.users) && team.users.includes(userId)
        );
        
        if (userTeams.length > 0) {
          return true;
        }
      }
      
      // Check if user has created any teams
      const { data: createdTeams, error: createdTeamsError } = await supabase
        .from('Teams')
        .select('id')
        .eq('owner_profile_id', userId);
        
      if (!createdTeamsError && createdTeams && createdTeams.length > 0) {
        return true;
      }
      
      // User has no teams
      return false;
    } catch (err) {
      return false;
    }
  };

  // Redirect if not signed in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }

    try {
      const success = await updateProfile({
        first_name: firstName,
        last_name: lastName
      });

      if (!success) {
        throw new Error('Failed to update profile');
      }

      // Check if user already has a team
      if (user) {
        const hasTeam = await checkUserTeam(user.id);
        
        if (hasTeam) {
          // User already has a team, skip team creation
          localStorage.removeItem('isSignUp'); // Clear sign-up flag
          setTimeout(() => {
            setLoading(false);
            navigate('/overview');
          }, 1000);
          return;
        }
      }

      // User doesn't have a team, move to the team creation step
      navigate('/create-team');
    } catch (err: any) {
      setError(err.message || 'Failed to save user information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignUpLayout
      title="Tell us about yourself"
      subtitle="We need a few more details to set up your account"
      currentStep={2}
      totalSteps={4}
    >
      <form onSubmit={handleSubmit} className="signup-form">
        <div className="signup-form-fields">
          <div className="signup-form-row">
            <Input
              label="First Name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              error={error}
              sizeVariant="large"
              required
            />
            
            <Input
              label="Last Name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
              sizeVariant="large"
            />
          </div>
        </div>
        
        <Button 
          type="submit" 
          className="signup-button"
          variant="primary"
          size="large"
          disabled={loading}
          isLoading={loading}
        >
          Continue
        </Button>
      </form>
    </SignUpLayout>
  );
};

export default UserInfo; 